import { memo, useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { SkeletonLoader, Tooltip } from '@geneui/components';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import { l } from 'constants/common';

interface PushNotificationInfoDetailsProps {
    appType: number;
    deviceTypes: number[];
    routeId: number;
}

const PushNotificationInfoDetails: FC<PushNotificationInfoDetailsProps> = ({ appType, deviceTypes, routeId }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [appTypeLabel, setAppTypeLabel] = useState<string>('');
    const [deviceTypeLabels, setDeviceTypeLabels] = useState<string>('');
    const [routeLabel, setRouteLabel] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);

    const { getPushNotificationConfig } = useCancelableRequests({
        getPushNotificationConfig: UtilsHttpService.getPushNotificationConfig,
    });

    const init = () => {
        doGetRequest(getPushNotificationConfig.request, {
            successCallback: (Data) => {
                const appTypeOption = Data.AppTypes.find((item) => item.Id === appType);

                if (appTypeOption) {
                    setAppTypeLabel(appTypeOption.Label);

                    const matchedDeviceTypes = appTypeOption.DeviceTypes.filter((dt) =>
                        deviceTypes.includes(dt.Id),
                    ).map((dt) => dt.Label);
                    setDeviceTypeLabels(matchedDeviceTypes.join(', '));

                    const matchedRoute = appTypeOption.Routes.find((r) => r.Id === routeId);
                    setRouteLabel(matchedRoute?.Label ?? t(l.NA));
                }

                setIsLoading(false);
            },
            errorCallback: () => {
                setIsLoading(false);
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <SkeletonLoader isBusy={isLoading}>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.AppType)}</span>
                <Tooltip text={appTypeLabel} position="left">
                    <span className="ellipsis-text kv-value kv-value-css">{appTypeLabel || t(l.NA)}</span>
                </Tooltip>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.DeviceType)}</span>
                <Tooltip text={deviceTypeLabels} position="left">
                    <span className="ellipsis-text kv-value kv-value-css">{deviceTypeLabels || t(l.NA)}</span>
                </Tooltip>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Route)}</span>
                <Tooltip text={routeLabel} position="left">
                    <span className="ellipsis-text kv-value kv-value-css">{routeLabel || t(l.NA)}</span>
                </Tooltip>
            </div>
        </SkeletonLoader>
    );
};

export default memo(PushNotificationInfoDetails);
